"use client";

import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { cn } from "@ui/lib";
import { Loader2 } from "lucide-react";
import type { LocationData } from "./types";

interface LocationInputProps {
  value?: LocationData;
  onChange: (location: LocationData | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /**
   * Geoapify result granularity. Defaults to `"city"` for backward
   * compatibility with all existing call sites (including the marketing
   * AI-credit-report flow at `apps/web/modules/marketing/ai-credit-report/ui/LocationInput.tsx`
   * which is a re-export of this component). Platform usages pass `"country"`
   * via `LocationInputWithToggle` to track at the national level.
   */
  type?: "city" | "country";
}

export function LocationInput({
  value,
  onChange,
  placeholder = "Select location...",
  className,
  disabled = false,
  type = "city",
}: LocationInputProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteRef = useRef<any>(null);
  const { theme, systemTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);

  // Hold the latest `onChange` and `value` in refs so the autocomplete-init
  // effect doesn't list them as dependencies. Re-running the init effect
  // tears down and recreates the Geoapify instance mid-interaction, which
  // races with React reconciliation and produces `Node.removeChild: The
  // node to be removed is not a child of this node`. Callers that pass an
  // inline arrow as `onChange` (a fresh reference on every render) would
  // otherwise trigger this on every keystroke/focus.
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // Ensure we're mounted before accessing theme
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !containerRef.current) return;

    const initializeAutocomplete = async () => {
      try {
        // Dynamically import the library and styles
        const { GeocoderAutocomplete } = await import('@geoapify/geocoder-autocomplete');
        
        // Determine if we should use dark mode
        const currentTheme = theme === 'system' ? systemTheme : theme;
        const isDarkMode = currentTheme === 'dark';
        
        // Import the appropriate stylesheet
        if (isDarkMode) {
          // @ts-ignore - CSS import
          await import('@geoapify/geocoder-autocomplete/styles/minimal-dark.css');
        } else {
          // @ts-ignore - CSS import
          await import('@geoapify/geocoder-autocomplete/styles/minimal.css');
        }

        // Add custom CSS to fix positioning issues
        if (!document.getElementById('geoapify-custom-styles')) {
          const style = document.createElement('style');
          style.id = 'geoapify-custom-styles';
          style.textContent = `
            .geoapify-autocomplete-items {
              position: absolute !important;
              top: 100% !important;
              left: 0 !important;
              right: 0 !important;
              z-index: 1000 !important;
              max-height: 200px !important;
              overflow-y: auto !important;
            }
          `;
          document.head.appendChild(style);
        }

        // Clean up existing autocomplete and DOM elements
        if (autocompleteRef.current) {
          try {
            if (typeof autocompleteRef.current.destroy === 'function') {
              autocompleteRef.current.destroy();
            } else if (typeof autocompleteRef.current.remove === 'function') {
              autocompleteRef.current.remove();
            }
          } catch (error) {
            console.warn('Error cleaning up autocomplete:', error);
          }
          autocompleteRef.current = null;
        }

        // Clean up any remaining Geoapify DOM elements
        if (containerRef.current) {
          const existingElements = containerRef.current.querySelectorAll('.geoapify-autocomplete-input, .geoapify-autocomplete-items, input');
          existingElements.forEach(element => {
            if (element.parentNode) {
              element.parentNode.removeChild(element);
            }
          });
          containerRef.current.innerHTML = '';
        }

        // Initialize the autocomplete
        const apiKey = process.env.NEXT_PUBLIC_GEOAPIFY_API_KEY;
        
        if (!apiKey) {
          console.warn('Geoapify API key not found. Please set NEXT_PUBLIC_GEOAPIFY_API_KEY');
          return;
        }

        if (containerRef.current) {
          try {
            // Let Geoapify create its own input element
            autocompleteRef.current = new GeocoderAutocomplete(
              containerRef.current,
              apiKey,
              {
                type,
                limit: 20,
                debounceDelay: 100,
                skipIcons: true,
                placeholder: placeholder,
              }
            );
          } catch (error) {
            console.error('Error creating GeocoderAutocomplete:', error);
            return;
          }

            // Apply our custom styling to the generated input
            const input = containerRef.current.querySelector('input');
            if (input) {
              input.className = cn(
                "flex h-10 w-full rounded-lg border border-input px-3 py-1 text-base transition-colors placeholder:text-foreground/60 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:border-ring focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
                className
              );
              input.disabled = disabled;

              // Show loading indicator on first focus to indicate lookup is initializing
              const handleFocus = () => {
                if (!hasBeenFocused) {
                  setHasBeenFocused(true);
                  setShowLoadingIndicator(true);
                  // Hide loading indicator after a short delay to indicate system is ready
                  setTimeout(() => {
                    setShowLoadingIndicator(false);
                  }, 1500);
                }
              };

              input.addEventListener('focus', handleFocus);

            // Add click handler to select all text when input has a value
            let inputHasBeenFocused = false;
            
            input.addEventListener('focus', () => {
              inputHasBeenFocused = true;
            });

            input.addEventListener('click', (e) => {
              const target = e.target as HTMLInputElement;
              if (target.value && target.value.trim()) {
                // Select all text when clicking on input with existing value
                setTimeout(() => {
                  target.select();
                }, 0);
              }
            });

            // Handle keyboard focus (tab navigation) - select all on first focus
            input.addEventListener('focus', (e) => {
              const target = e.target as HTMLInputElement;
              if (!inputHasBeenFocused && target.value && target.value.trim()) {
                setTimeout(() => {
                  target.select();
                }, 0);
              }
            });
          }

          // Fix positioning for the dropdown
          const container = containerRef.current;
          if (container) {
            // Ensure the container has relative positioning for proper dropdown placement
            container.style.position = 'relative';
            container.style.zIndex = '50';
          }

          // Handle location selection
          try {
            autocompleteRef.current.on('select', (location: any) => {
              if (location) {
                // Extract location data from Geoapify response. For `type="country"`
                // the result has no city/state and we explicitly clear those fields
                // so the persisted LocationData is country-only.
                const locationData: LocationData =
                  type === "country"
                    ? {
                        country: location.properties?.country_code?.toLowerCase(),
                      }
                    : {
                        city: location.properties?.city || location.properties?.name,
                        region: location.properties?.state || location.properties?.county,
                        country: location.properties?.country_code?.toLowerCase(),
                      };

                onChangeRef.current(locationData);

                // Clean up any duplicate elements that might have been created
                setTimeout(() => {
                  if (containerRef.current) {
                    const dropdowns = containerRef.current.querySelectorAll('.geoapify-autocomplete-items');
                    const inputs = containerRef.current.querySelectorAll('input');
                    
                    // Remove extra dropdowns (keep only the first one if any)
                    for (let i = 1; i < dropdowns.length; i++) {
                      const parent = dropdowns[i]?.parentNode;
                      if (parent) {
                        parent.removeChild(dropdowns[i]);
                      }
                    }
                    
                    // Remove extra inputs (keep only the first one)
                    for (let i = 1; i < inputs.length; i++) {
                      const parent = inputs[i]?.parentNode;
                      if (parent) {
                        parent.removeChild(inputs[i]);
                      }
                    }
                  }
                }, 100);
              }
            });

            // Handle input clear
            autocompleteRef.current.on('input', (query: string) => {
              if (!query.trim()) {
                onChangeRef.current(undefined);
              }
            });
          } catch (error) {
            console.error('Error setting up autocomplete event listeners:', error);
          }
        }

      } catch (error) {
        console.error('Error initializing autocomplete:', error);
      }

      // Set initial value if exists (read from the ref so we don't depend on
      // `value` in the effect's dep list — see note at the top of the file).
      const initial = valueRef.current;
      if (initial && containerRef.current) {
        const displayValue = [
          initial.city,
          initial.region,
          initial.country?.toUpperCase()
        ].filter(Boolean).join(', ');

        const input = containerRef.current.querySelector('input');
        if (input) {
          input.value = displayValue;
        }
      }
    };

    initializeAutocomplete().catch(console.error);

    // Cleanup on unmount
    return () => {
      if (autocompleteRef.current) {
        try {
          if (typeof autocompleteRef.current.destroy === 'function') {
            autocompleteRef.current.destroy();
          } else if (typeof autocompleteRef.current.remove === 'function') {
            autocompleteRef.current.remove();
          }
        } catch (error) {
          console.warn('Error cleaning up autocomplete on unmount:', error);
        }
        autocompleteRef.current = null;
      }

      // Clean up any remaining Geoapify DOM elements
      if (containerRef.current) {
        const existingElements = containerRef.current.querySelectorAll('.geoapify-autocomplete-input, .geoapify-autocomplete-items, input');
        existingElements.forEach(element => {
          try {
            if (element.parentNode) {
              element.parentNode.removeChild(element);
            }
          } catch (error) {
            // Element might already be removed
          }
        });
      }
    };
    // `onChange` and `value` deliberately excluded — they're read via refs
    // inside the effect to avoid re-instantiating Geoapify on every parent
    // render. See the refs at the top of the component.
  }, [mounted, theme, systemTheme, placeholder, type]);

  // Don't render until mounted to avoid hydration issues
  if (!mounted) {
    return (
      <div className={cn("w-full relative", className)}>
        <input
          className="flex h-10 w-full rounded-lg border border-input px-3 py-1 text-base transition-colors placeholder:text-foreground/60 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:border-ring focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      className={cn("w-full relative", className)}
    >
      {showLoadingIndicator && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
